/**
 * Company Profile — Versioning System
 * ====================================
 *
 * Goal: Update the FR/EN Company Profile PDFs without breaking any
 * existing inbound links (emails, social media, partner sites, etc.).
 *
 * Strategy: "Stable canonical URL + immutable archive"
 * ----------------------------------------------------
 *  - The CANONICAL files in `public/documents/` always serve the
 *    LATEST version and keep a stable, never-changing filename:
 *      • /documents/MTB_BTP_Company_Profile_FR.pdf   (latest FR)
 *      • /documents/MTB_BTP_Company_Profile_EN.pdf   (latest EN)
 *
 *  - Every previous version is preserved as an immutable archive
 *    under `public/documents/archive/` with a versioned suffix:
 *      • /documents/archive/MTB_BTP_Company_Profile_FR_v1.0.0.pdf
 *      • /documents/archive/MTB_BTP_Company_Profile_EN_v1.0.0.pdf
 *
 * How to publish a new version (operational checklist)
 * ----------------------------------------------------
 *  1. Move the current canonical PDFs to the archive folder, renaming
 *     them with the *current* version suffix from `CURRENT_VERSION`
 *     below (e.g. `_v1.0.0`).
 *  2. Drop the new PDFs in `public/documents/` using the canonical
 *     filenames (no suffix). Keep the exact same filename!
 *  3. Bump `CURRENT_VERSION` and `RELEASE_DATE` in this file.
 *  4. Append the previous version to `VERSION_HISTORY` so it remains
 *     accessible from the UI for transparency / audit.
 *
 * Result:
 *  - Public links never break.
 *  - All old versions stay reachable via stable archive URLs.
 *  - The displayed version + release date update everywhere the
 *    component reads from this constant (preview modal, footer…).
 *
 * Cache busting:
 *  - Browsers / CDNs may cache the canonical PDF aggressively. We
 *    expose `getProfileUrl(lang, { bustCache })` which appends `?v=`
 *    so freshly-published versions are fetched immediately after
 *    a release, without renaming the canonical file.
 */

export type ProfileLang = 'fr' | 'en';

export interface ProfileVersion {
  /** Semver-like label, e.g. "2.1.0" */
  version: string;
  /** ISO date (YYYY-MM-DD) of the release */
  releaseDate: string;
  /** Short bilingual changelog shown in the version history */
  notes: {
    fr: string;
    en: string;
  };
}

/**
 * Latest published version of the Company Profile.
 * Bump this whenever you replace the canonical PDFs.
 */
export const CURRENT_VERSION: ProfileVersion = {
  version: '1.0.0',
  releaseDate: '2026-04-23',
  notes: {
    fr: 'Première édition officielle — références, certifications et capacités MTB BTP.',
    en: 'First official edition — MTB BTP references, certifications and capabilities.',
  },
};

/**
 * Past versions, ordered from newest to oldest.
 * When you release a new version, MOVE the previous PDFs to
 * `public/documents/archive/` with the suffix `_v{version}` and
 * register them here.
 */
export const VERSION_HISTORY: ProfileVersion[] = [
  // Example — uncomment & populate when releasing v2.0.0:
  // {
  //   version: '1.0.0',
  //   releaseDate: '2026-04-23',
  //   notes: {
  //     fr: 'Première édition officielle.',
  //     en: 'First official edition.',
  //   },
  // },
];

/** Stable canonical filenames — DO NOT change these. */
const CANONICAL_FILENAMES: Record<ProfileLang, string> = {
  fr: 'MTB_BTP_Company_Profile_FR.pdf',
  en: 'MTB_BTP_Company_Profile_EN.pdf',
};

const CANONICAL_DIR = '/documents';
const ARCHIVE_DIR = '/documents/archive';

/**
 * Returns the stable canonical URL for the LATEST version of a PDF.
 * This URL never changes across releases — safe to share externally.
 *
 * @param lang   "fr" | "en"
 * @param opts.bustCache  Append `?v={CURRENT_VERSION.version}` to force
 *                        browsers/CDNs to fetch a freshly released file
 *                        (recommended for in-app links). Defaults to true.
 */
export function getProfileUrl(
  lang: ProfileLang,
  opts: { bustCache?: boolean } = {},
): string {
  const { bustCache = true } = opts;
  const base = `${CANONICAL_DIR}/${CANONICAL_FILENAMES[lang]}`;
  return bustCache ? `${base}?v=${CURRENT_VERSION.version}` : base;
}

/**
 * Returns the suggested filename to use when triggering a download
 * (used as the `download` attribute on <a> tags).
 * Includes the version suffix so users can keep multiple editions
 * side-by-side in their downloads folder.
 */
export function getProfileDownloadName(lang: ProfileLang): string {
  return `MTB_BTP_Company_Profile_${lang.toUpperCase()}_v${CURRENT_VERSION.version}.pdf`;
}

/**
 * Returns the URL of an archived (previous) version, if it exists in
 * the `public/documents/archive/` folder.
 */
export function getArchivedProfileUrl(
  lang: ProfileLang,
  version: string,
): string {
  const base = CANONICAL_FILENAMES[lang].replace('.pdf', `_v${version}.pdf`);
  return `${ARCHIVE_DIR}/${base}`;
}
